// Centralized, display-only date/time localization foundation.
//
// This module intentionally does NOT import the live i18n singleton
// (src/i18n.js) - every exported function takes the active language as
// an explicit `language` argument instead. Callers (components using
// react-i18next's useTranslation()) are expected to pass
// `i18n.resolvedLanguage || i18n.language`. This keeps the module
// side-effect-free and trivially testable without mounting the real
// i18n instance, and avoids any risk of an import cycle with it.
//
// `normalizeSupportedLang`/`SUPPORTED_LANGS` are reused from
// src/utils/languagePref.js (a dependency-free module - importing it
// here introduces no import cycle) so the language-code normalization
// rules stay identical to the rest of the app instead of being
// reimplemented and risking drift.
//
// This is a FOUNDATION ONLY: no existing UI consumer is migrated to it
// in this patch, and formatDateTimeEn (src/utils/digits.js) is left
// untouched. Relative-time wording ("X minutes ago") is out of scope
// here as well.
//
// parseBirthDateParts/calculateAgeFromCalendarParts/
// calculateAgeFromBirthDateString below ARE consumed - by
// SelectGenderPage.js (registration), MyProfilePage.js (own profile),
// UserProfilePage.js (public profile), and EditProfileForm.js - as the
// single shared, timezone-safe implementation for the canonical
// birthDate field. See each function's own doc comment for the exact
// policy, which is deliberately identical to
// functions/auth/completeOnboarding.js's server-side parseBirthDate/
// isAdult18 (same regex, same Date.UTC round-trip validation, same
// UTC-vs-UTC "now" comparison) so every viewer, in every timezone, sees
// the exact same age for the exact same stored value - matching the
// server's own authoritative policy rather than inventing a new one.

import { normalizeSupportedLang, SUPPORTED_LANGS } from "./languagePref";

// Maps LIVORA i18n language codes to real BCP-47 locale tags for
// Intl.DateTimeFormat. "ph" (Filipino, this app's key) is not a valid
// ISO 639 language code, so it is mapped to "fil". Every other
// supported code already IS its own valid Intl locale identifier.
// Derived from SUPPORTED_LANGS (rather than a separately hand-maintained
// list) so this table can never silently drift out of sync with the
// app's actual supported-language set.
const INTL_LOCALE_OVERRIDES = { ph: "fil" };

const INTL_LOCALE_BY_LANG = SUPPORTED_LANGS.reduce((acc, lang) => {
  acc[lang] = INTL_LOCALE_OVERRIDES[lang] || lang;
  return acc;
}, {});

const FALLBACK_LANG = "en";

// Matches a bare "YYYY-MM-DD" value (the whole string, nothing else -
// the shape the canonical birthDate field is stored as).
const ISO_DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
// Matches just the leading "YYYY-MM-DD" of any longer string (e.g. a
// full ISO datetime such as "2026-08-23T15:45:00Z") so its calendar-date
// portion can be validated the same strict way before the full string
// is ever handed to the native Date parser.
const ISO_DATE_PREFIX_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Single shared strict-validation helper, used for BOTH a bare
 * "YYYY-MM-DD" value and the leading calendar-date portion of a full
 * ISO datetime string. JavaScript's Date constructor silently rolls an
 * out-of-range day/month forward into the next month/year instead of
 * rejecting it - Date.UTC(2026, 1, 30) ("2026-02-30") quietly becomes
 * March 2nd, and this rollover happens for the date PREFIX of a full
 * ISO datetime string too (`new Date("2026-02-30T12:00:00Z")` silently
 * parses as March 2nd, not an Invalid Date). This helper is the one
 * place that catches that: after constructing a UTC Date from the
 * numeric year/month/day, it reads the UTC year/month/day back off the
 * result and compares them against the original numbers - any mismatch
 * (bad day-of-month, bad month, Feb 29 on a non-leap year, etc.) means
 * the input wasn't a real calendar date at all.
 */
function isRealCalendarDate(year, month, day) {
  const date = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(date.getTime())) return false;
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() + 1 === month &&
    date.getUTCDate() === day
  );
}

/**
 * Safely resolves an arbitrary input value into a { date, dateOnly }
 * pair, or null if the value is missing/malformed/unparseable. Never
 * throws.
 *
 * dateOnly is true only for a bare "YYYY-MM-DD" string (the shape the
 * app's canonical birthDate field is stored as) - those are parsed via
 * Date.UTC(year, month-1, day) instead of the platform's local-timezone
 * Date constructor, specifically so a caller running in a timezone
 * behind UTC (e.g. anywhere in the Americas) never has the calendar
 * day silently shift backward by one when the value is later formatted.
 * Every other accepted shape already carries real time-of-day/timezone
 * information and is left as-is.
 *
 * Both a bare "YYYY-MM-DD" value AND the leading calendar-date portion
 * of a longer ISO datetime string are validated through the shared
 * isRealCalendarDate() helper above - an invalid calendar date (e.g.
 * "2026-02-30T12:00:00Z") is rejected as null rather than silently
 * accepted as whatever unrelated date the native Date parser would
 * otherwise roll it over to.
 */
function parseValue(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : { date: value, dateOnly: false };
  }

  if (typeof value === "object" && typeof value.toDate === "function") {
    try {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime())
        ? { date: d, dateOnly: false }
        : null;
    } catch {
      return null;
    }
  }

  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : { date: d, dateOnly: false };
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const isoOnlyMatch = ISO_DATE_ONLY_RE.exec(trimmed);
    if (isoOnlyMatch) {
      const [, y, m, d] = isoOnlyMatch;
      const year = Number(y);
      const month = Number(m);
      const day = Number(d);
      if (!isRealCalendarDate(year, month, day)) return null;
      return { date: new Date(Date.UTC(year, month - 1, day)), dateOnly: true };
    }

    const prefixMatch = ISO_DATE_PREFIX_RE.exec(trimmed);
    if (prefixMatch) {
      const [, y, m, d] = prefixMatch;
      if (!isRealCalendarDate(Number(y), Number(m), Number(d))) return null;
    }

    const d = new Date(trimmed);
    return Number.isNaN(d.getTime()) ? null : { date: d, dateOnly: false };
  }

  return null;
}

/**
 * Strictly parses a bare "YYYY-MM-DD" calendar-only birth date into
 * numeric { year, month, day } (month is 1-indexed), or null for
 * anything else: null/undefined/empty, malformed text, trailing/leading
 * content beyond the exact "YYYY-MM-DD" shape (the regex is anchored
 * with ^...$, unlike ISO_DATE_PREFIX_RE above which deliberately allows
 * a longer string), an impossible calendar date, or an invalid leap day
 * (e.g. "2001-02-29") - reusing the same isRealCalendarDate() rollover
 * check the rest of this module already relies on, so this can never
 * drift from it.
 *
 * Deliberately never constructs `new Date(value)` from the raw string -
 * only Date.UTC(year, month - 1, day) with already-validated numeric
 * parts, exactly like functions/auth/completeOnboarding.js's
 * parseBirthDate. This is what makes it safe to call from any caller
 * timezone.
 */
export function parseBirthDateParts(value) {
  if (typeof value !== "string") return null;
  const match = ISO_DATE_ONLY_RE.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!isRealCalendarDate(year, month, day)) return null;

  return { year, month, day };
}

/**
 * Full-years age from already-validated { year, month, day } calendar
 * parts, evaluated entirely against the UTC calendar year/month/day of
 * `now` - identical comparison policy to
 * functions/auth/completeOnboarding.js's isAdult18 (same month-delta/
 * day-of-month check, same UTC getters on `now`), generalized to return
 * the actual age instead of only an 18+ boolean. This is also the exact
 * source of this module's leap-day-birthday rule: a "2000-02-29" birth's
 * age only increments once the UTC calendar reaches month=2 day=29 (a
 * leap year) or month=3 day>=1 (a non-leap year, where day 29 of
 * February never occurs, so the "not yet occurred" condition only
 * clears once March begins) - inspected from, and intentionally
 * identical to, the server's own already-correct behavior, not a new
 * client rule.
 *
 * Returns null (never 0, never a negative number, never NaN) for a
 * missing calendarDate or a birth date that has not yet occurred as of
 * `now` (a future birth date) - the caller must never display an
 * invented age.
 */
export function calculateAgeFromCalendarParts(calendarDate, now = new Date()) {
  if (!calendarDate) return null;
  const { year, month, day } = calendarDate;

  const nowYear = now.getUTCFullYear();
  const nowMonth = now.getUTCMonth(); // 0-indexed
  const nowDay = now.getUTCDate();

  const birthUtcMs = Date.UTC(year, month - 1, day);
  const nowUtcMs = Date.UTC(nowYear, nowMonth, nowDay);
  if (birthUtcMs > nowUtcMs) {
    return null;
  }

  let age = nowYear - year;
  const monthDelta = nowMonth - (month - 1);
  if (monthDelta < 0 || (monthDelta === 0 && nowDay < day)) {
    age -= 1;
  }
  return age;
}

/**
 * Convenience: raw "YYYY-MM-DD" string straight to a full-years age, or
 * null for anything parseBirthDateParts rejects or any future date. This
 * is the one function registration/profile callers should use - it never
 * parses the string through `new Date(value)`.
 */
export function calculateAgeFromBirthDateString(value, now = new Date()) {
  const parts = parseBirthDateParts(value);
  if (!parts) return null;
  return calculateAgeFromCalendarParts(parts, now);
}

/**
 * Resolves the active LIVORA language code to an Intl locale tag,
 * falling back to English for any missing/unrecognized code.
 */
function resolveIntlLocale(language) {
  const normalized = normalizeSupportedLang(language) || FALLBACK_LANG;
  return INTL_LOCALE_BY_LANG[normalized] || FALLBACK_LANG;
}

/**
 * Numeral policy: every supported language renders plain Latin 0-9
 * digits - this includes Arabic, which otherwise defaults to
 * Arabic-Indic digits under Intl, and Persian/Hebrew/Kurdish, which
 * would otherwise inherit their own non-Latin numbering systems from
 * Intl's locale defaults too. Arabic still gets real Arabic language
 * formatting - month names, word order, and RTL direction all come
 * from the "ar" locale tag as normal - only the digit glyphs are
 * pinned to Latin. This function ignores its `language` argument
 * entirely (kept only so call sites read naturally / for future
 * language-specific policy if one is ever needed) and always returns
 * "latn". Applied AFTER any caller-supplied options (see
 * formatWithIntl) so it is always this fixed policy, never a per-call
 * override - a caller passing numberingSystem: "arab" or "arabext" has
 * no effect.
 */
function resolveNumberingSystem(_language) {
  return "latn";
}

function formatWithIntl(value, language, defaultOptions, overrideOptions) {
  const parsed = parseValue(value);
  if (!parsed) return "";

  const locale = resolveIntlLocale(language);
  const options = { ...defaultOptions, ...(overrideOptions || {}) };

  // A bare "YYYY-MM-DD" is a CALENDAR DATE, not an instant in time - it
  // has no associated timezone at all, so there is no meaningful
  // "caller's timezone" to honor for it. Applied AFTER merging caller
  // options, unconditionally overwriting any timeZone the caller passed
  // - a caller must never be able to shift a birthdate (or any other
  // date-only value) to the previous or next day by passing a timeZone
  // option. Real instants (Date, epoch ms, full ISO datetime strings,
  // Firestore Timestamp-like values) are unaffected by this branch and
  // keep honoring a caller-supplied timeZone normally.
  if (parsed.dateOnly) {
    options.timeZone = "UTC";
  }

  // Numeral policy is a fixed invariant, Latin digits for every
  // supported language including Arabic - always applied last, never
  // left to a caller-supplied option to override.
  options.numberingSystem = resolveNumberingSystem(language);

  try {
    return new Intl.DateTimeFormat(locale, options).format(parsed.date);
  } catch {
    return "";
  }
}

/** Date only, e.g. "Aug 23, 2026" (locale-formatted, options-overridable). */
export function formatLocalizedDate(value, language, options) {
  return formatWithIntl(
    value,
    language,
    { year: "numeric", month: "short", day: "numeric" },
    options
  );
}

/** Date + time, e.g. "Aug 23, 2026, 03:45 PM". */
export function formatLocalizedDateTime(value, language, options) {
  return formatWithIntl(
    value,
    language,
    { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" },
    options
  );
}

/** Time only, e.g. "03:45 PM". */
export function formatLocalizedTime(value, language, options) {
  return formatWithIntl(value, language, { hour: "2-digit", minute: "2-digit" }, options);
}

/**
 * Display-only formatter for the canonical birthDate field (stored as a
 * plain "YYYY-MM-DD" string). Defaults match the numeric day/month/year
 * shape a birthdate is conventionally shown in; never mutates or
 * re-derives the stored value - this is presentation only.
 */
export function formatBirthDateDisplay(value, language, options) {
  return formatWithIntl(
    value,
    language,
    { year: "numeric", month: "2-digit", day: "2-digit" },
    options
  );
}
